import { rebuildResults } from "./checksheets";
// ─── Send to quote ────────────────────────────────────────────────────────────
// Packs the sheet's details and findings (defects + other notes, with photos)
// into one file the Workshop Quotes app can open with "New quote from checksheet".

const safe = s => String(s || "").replace(/[\\/:*?"<>|]+/g, "").trim();

export function buildQuoteExport(config, header, state) {
  const findings = [];
  (state.defects || []).filter(d => d.defect).forEach(d => {
    findings.push({
      key: d.failKey, source: "defect",
      title: d.defect, recommendation: d.recommendation || "",
      photos: (d.photos || []).filter(Boolean),
    });
  });
  (state.otherNotes || []).filter(n => (n.text || "").trim() || (n.photos || []).length).forEach((n, i) => {
    findings.push({
      key: `note-${i}`, source: "note",
      title: (n.text || "").trim() || "Other note", recommendation: n.recommendation || "",
      photos: (n.photos || []).filter(Boolean),
    });
  });
  return {
    format: "waimea-checksheet", version: 1, exportedAt: new Date().toISOString(),
    sheet: { label: config.label || "", equipment: config.equipment || "", service: config.service || "" },
    header: {
      jobno: header.jobno || "", date: header.date || "", tech: header.tech || "",
      customer: header.customer || "", contact: header.contact || "",
      make: header.make || "", model: header.model || "", modelVariant: header.modelVariant || "",
      serial: header.serial || "", tankerModel: header.tankerModel || "", tankerSerial: header.tankerSerial || "",
      rego: header.rego || "", odo: header.odo || "", enghours: header.enghours || "", pto: header.pto || "",
    },
    findings,
    // Full pre-rebuild results, so a Post-rebuild sheet can import this PDF and compare
    ...(config.service === "PR" ? { rebuild: rebuildResults(config, header, state) } : {}),
    // Pre-rebuild readings, shown on the quote for reference
    tests: (config.measurements || []).map(g => ({
      title: g.title,
      items: g.items.map(it => ({ label: it.label, value: (state.measures||{})[it.key]==="na" ? "N/A" : ((state.measures||{})[it.key] ? `${state.measures[it.key]} ${it.unit}` : "") })),
    })),
  };
}

export async function sendToQuote(config, header, state) {
  const data = buildQuoteExport(config, header, state);
  if (!data.findings.length) {
    alert("No defects or notes on this sheet yet – fail an item or add a note first.");
    return null;
  }
  const name = `Checksheet ${safe(header.jobno) || "draft"} ${safe(header.customer)}`.trim() + ".json";
  const file = new File([JSON.stringify(data)], name, { type: "application/json" });
  // Phones/tablets: open the share sheet (Teams, email…). Desktop: download.
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: name, text: "Checksheet for quoting" });
      return name;
    }
  } catch (e) {
    if (e && e.name === "AbortError") return null;
  }
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return name;
}

// ─── Hidden quote data inside the checksheet PDF ─────────────────────────────
// The PDF that gets filed in WFM job docs also carries the quote data (header,
// defects, notes, recommendations, photos) as an extra, unreferenced PDF object.
// PDF viewers ignore it; the Workshop Quotes app finds it between the markers.
export const QUOTE_DATA_BEGIN = "%WAIMEA-QUOTE-DATA-BEGIN%";
export const QUOTE_DATA_END   = "%WAIMEA-QUOTE-DATA-END%";

export function embedQuoteData(doc, config, header, state) {
  const data = buildQuoteExport(config, header, state);
  // ASCII-only JSON so it can be written straight into the PDF byte stream.
  const json = JSON.stringify(data).replace(/[\u007f-\uffff]/g, c => "\\u" + c.charCodeAt(0).toString(16).padStart(4, "0"));
  const body = QUOTE_DATA_BEGIN + json + QUOTE_DATA_END;
  doc.internal.events.subscribe("postPutResources", () => {
    doc.internal.newObject();
    doc.internal.out(`<< /Type /WaimeaQuoteData /Length ${body.length} >>`);
    doc.internal.out("stream");
    doc.internal.out(body);
    doc.internal.out("endstream");
    doc.internal.out("endobj");
  });
  return data.findings.length;
}

// Read the pre-rebuild results back out of a checksheet PDF (for Post-rebuild).
// Resolves { rebuild, header } or throws with a message a tech can act on.
export async function readRebuildFromPdf(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let text = "";
  for (let i = 0; i < bytes.length; i += 65536) text += String.fromCharCode.apply(null, bytes.subarray(i, i + 65536));
  const start = text.lastIndexOf(QUOTE_DATA_BEGIN), end = text.lastIndexOf(QUOTE_DATA_END);
  if (start === -1 || end <= start) throw new Error("This PDF has no checksheet data in it. Pick the Pre-rebuild PDF downloaded from the checksheet app.");
  const data = JSON.parse(text.slice(start + QUOTE_DATA_BEGIN.length, end));
  if (data.sheet?.service !== "PR") throw new Error(`That PDF is from a ${data.sheet?.label || "different"} sheet, not a Pre-rebuild. Pick the Pre-rebuild PDF.`);
  if (!data.rebuild) throw new Error("This Pre-rebuild PDF was made before the app could carry its results (before v61). Its figures can't be imported.");
  return { rebuild: data.rebuild, header: data.header || {} };
}
